/**
 * Folder actions (DESIGN.md §18) for the folder tree: context menu and the "…" button per folder.
 * Default folders (Inbox, Sent Items …) cannot be renamed, moved or deleted; only Deleted Items
 * and Junk Email can be emptied.
 */
import { defineActions, type ActionRegistry } from '@platform/ui';
import { CheckCheckIcon, FileUpIcon, FolderInputIcon, FolderPlusIcon, PencilIcon, StarIcon, Trash2Icon, type LucideIcon } from 'lucide-react';
import type { Folder } from '../api';

export interface FolderActionHandlers {
  create(parent: Folder): void;
  rename(folder: Folder): void;
  importMessages(folder: Folder): void;
  markAllRead(folder: Folder): void;
  empty(folder: Folder): void | Promise<void>;
  remove(folder: Folder): void | Promise<void>;
  isFavourite?(folder: Folder): boolean;
  toggleFavourite?(folder: Folder): void;
}

export interface FolderActionOptions {
  /** Server capabilities (`WebmailApi` optional methods). */
  canCreate: boolean;
  canImport: boolean;
  canRename: boolean;
  canDelete: boolean;
  canMarkAllRead: boolean;
  canEmpty: boolean;
}

const EMPTYABLE = new Set<Folder['wellKnown']>(['deleted', 'junk']);
const custom = (folder: Folder) => folder.wellKnown === undefined;

export function createFolderActions(handlers: FolderActionHandlers, options: FolderActionOptions): ActionRegistry<Folder> {
  const surfaces = ['overflow', 'context'] as const;
  const folderOf = (targets: readonly Folder[]) => targets[0]!;
  const action = (id: string, icon: LucideIcon) => ({ id: `folder.${id}`, icon, surfaces });
  return defineActions<Folder>([
    {
      ...action('new', FolderPlusIcon),
      label: 'webmail.folderActions.newFolder',
      group: 'edit',
      planned: !options.canCreate,
      run: ({ targets }) => handlers.create(folderOf(targets)),
    },
    {
      ...action('import', FileUpIcon),
      label: 'webmail.folderActions.importMessages',
      group: 'edit',
      planned: !options.canImport,
      run: ({ targets }) => handlers.importMessages(folderOf(targets)),
    },
    {
      ...action('rename', PencilIcon),
      label: 'webmail.folderActions.rename',
      group: 'edit',
      visible: ({ targets }) => custom(folderOf(targets)),
      planned: !options.canRename,
      run: ({ targets }) => handlers.rename(folderOf(targets)),
    },
    {
      ...action('move', FolderInputIcon),
      label: 'webmail.folderActions.moveFolder',
      group: 'edit',
      visible: ({ targets }) => custom(folderOf(targets)),
      planned: true,
      run: () => undefined,
    },
    ...(handlers.toggleFavourite
      ? [{
          ...action('favourite', StarIcon),
          label: 'webmail.folderActions.addFavourite' as const,
          labelFor: ({ targets }: { targets: readonly Folder[] }) => handlers.isFavourite?.(folderOf(targets))
            ? 'webmail.folderActions.removeFavourite' as const
            : 'webmail.folderActions.addFavourite' as const,
          group: 'state',
          visible: ({ targets }: { targets: readonly Folder[] }) => !folderOf(targets).mailboxRoot,
          run: ({ targets }: { targets: readonly Folder[] }) => handlers.toggleFavourite?.(folderOf(targets)),
        }]
      : []),
    {
      ...action('markAllRead', CheckCheckIcon),
      label: 'webmail.folderActions.markAllRead',
      group: 'state',
      enabled: ({ targets }) => folderOf(targets).unread > 0,
      planned: !options.canMarkAllRead,
      run: ({ targets }) => handlers.markAllRead(folderOf(targets)),
    },
    {
      ...action('empty', Trash2Icon),
      label: 'webmail.folderActions.empty',
      group: 'delete',
      danger: true,
      visible: ({ targets }) => EMPTYABLE.has(folderOf(targets).wellKnown),
      enabled: ({ targets }) => folderOf(targets).total > 0,
      planned: !options.canEmpty,
      confirm: ({ targets }) => {
        const params = { name: folderOf(targets).name };
        return {
          title: { key: 'webmail.folderActions.emptyTitle', params },
          description: { key: 'webmail.folderActions.emptyDescription', params },
          confirm: 'webmail.folderActions.empty',
        };
      },
      run: ({ targets }) => handlers.empty(folderOf(targets)),
    },
    {
      ...action('delete', Trash2Icon),
      label: 'webmail.folderActions.delete',
      group: 'delete',
      danger: true,
      visible: ({ targets }) => custom(folderOf(targets)),
      planned: !options.canDelete,
      confirm: ({ targets }) => ({
        title: { key: 'webmail.folderActions.deleteTitle', params: { name: folderOf(targets).name } },
        description: 'webmail.folderActions.deleteDescription',
        confirm: 'webmail.folderActions.delete',
      }),
      run: ({ targets }) => handlers.remove(folderOf(targets)),
    },
  ]);
}
